/**
 * Ashtamsha (D8) Vara Ruler Calculator
 * Based on Vedic principles for calculating planetary rulers of 8 divisions of the day
 */

/**
 * Planetary order for Ashtamsha D8 system (8 planets including Rahu)
 * Based on the algorithm: (day_of_week + part_number - 2) mod 8
 */
export const ASHTAMSHA_PLANETARY_ORDER: string[] = [
  'Surya',     // 0: Sun
  'Chandra',   // 1: Moon
  'Mangala',   // 2: Mars
  'Budha',     // 3: Mercury
  'Guru',      // 4: Jupiter
  'Shukra',    // 5: Venus
  'Shani',     // 6: Saturn
  'Rahu'       // 7: North Node
];

/**
 * Weekday to planetary lord mapping (API format)
 * Uses API vara_number directly: 0=Sunday, 1=Monday, ..., 6=Saturday
 */
export const ASHTAMSHA_WEEKDAY_LORDS: Record<number, string> = {
  0: 'Surya',      // Sunday (Ravivaar)
  1: 'Chandra',    // Monday (Somvaar)
  2: 'Mangala',    // Tuesday (Mangalvaar)
  3: 'Budha',      // Wednesday (Budhvaar)
  4: 'Guru',       // Thursday (Guruvaar)
  5: 'Shukra',     // Friday (Shukravaar)
  6: 'Shani'       // Saturday (Shanivaar)
};

/**
 * Ashtamsha ruler lookup table based on the documentation table
 * varaNumber: 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
 * sectorNumber: 1-8 (parts of day)
 */
const ASHTAMSHA_RULER_TABLE: Record<number, string[]> = {
  0: ['Shani', 'Shukra', 'Guru', 'Budha', 'Mangala', 'Chandra', 'Surya', 'Rahu'], // Sunday
  1: ['Surya', 'Rahu', 'Shani', 'Shukra', 'Guru', 'Budha', 'Mangala', 'Chandra'], // Monday
  2: ['Shukra', 'Guru', 'Budha', 'Mangala', 'Chandra', 'Surya', 'Rahu', 'Shani'], // Tuesday
  3: ['Budha', 'Mangala', 'Chandra', 'Surya', 'Rahu', 'Shani', 'Shukra', 'Guru'], // Wednesday
  4: ['Guru', 'Budha', 'Mangala', 'Chandra', 'Surya', 'Rahu', 'Shani', 'Shukra'], // Thursday
  5: ['Mangala', 'Chandra', 'Surya', 'Rahu', 'Shani', 'Shukra', 'Guru', 'Budha'], // Friday
  6: ['Chandra', 'Surya', 'Rahu', 'Shani', 'Shukra', 'Guru', 'Budha', 'Mangala']  // Saturday
};

/**
 * Calculate Ashtamsha (D8) Vara ruler using the documentation table
 *
 * @param sectorNumber - Sector number (1-8)
 * @param varaNumber - API vara_number (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns Name of ruling Graha
 * @throws Error if sectorNumber or varaNumber is out of valid range
 */
export function getAshtamshaGrahaRuler(sectorNumber: number, varaNumber: number): string {
  if (sectorNumber < 1 || sectorNumber > 8) {
    throw new Error(`Ashtamsha sector number must be 1-8, got ${sectorNumber}`);
  }

  if (varaNumber < 0 || varaNumber > 6) {
    throw new Error(`Vara number must be 0-6, got ${varaNumber}`);
  }

  // Get ruler from lookup table (convert 1-based sector to 0-based array index)
  const rulers = ASHTAMSHA_RULER_TABLE[varaNumber];
  return rulers[sectorNumber - 1];
}

/**
 * Get planet code from Sanskrit name (reuse from muhurtaRulers)
 * @param planetName - Full Sanskrit planet name
 * @returns 2-character planet code
 */
export function getAshtamshaPlanetCode(planetName: string): string {
  // Import and reuse the existing mapping from muhurtaRulers
  const codeMap: Record<string, string> = {
    'Surya': 'Sy',
    'Chandra': 'Ch',
    'Mangala': 'Ma',
    'Budha': 'Bu',
    'Guru': 'Gu',
    'Shukra': 'Sk',
    'Shani': 'Sa',
    'Rahu': 'Ra'
  };

  return codeMap[planetName] || planetName.substring(0, 2);
}

/**
 * Calculate Ashtamsha ruler information for a specific sector
 * @param sectorNumber - Sector number (1-8)
 * @param varaNumber - API vara_number (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns Object with ruler name and code
 */
export function getAshtamshaSectorRuler(sectorNumber: number, varaNumber: number): {
  ruler: string;
  code: string;
} {
  const ruler = getAshtamshaGrahaRuler(sectorNumber, varaNumber);
  const code = getAshtamshaPlanetCode(ruler);

  return { ruler, code };
}

/**
 * Generate all 8 sectors with rulers for a given day
 * @param varaNumber - API vara_number (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns Array of 8 sectors with ruler information
 */
export function generateAshtamshaSectors(varaNumber: number): Array<{
  index: number;
  startDegree: number;
  endDegree: number;
  ruler: string;
  rulerCode: string;
}> {
  const sectors = [];

  for (let i = 1; i <= 8; i++) {
    const { ruler, code } = getAshtamshaSectorRuler(i, varaNumber);

    sectors.push({
      index: i,
      startDegree: (i - 1) * 45, // Each sector is 45°
      endDegree: i * 45,
      ruler: ruler,
      rulerCode: code
    });
  }

  return sectors;
}